import { useMutation } from '@apollo/client';
import { Stack, Typography } from '@ui-components/neo.tax';
import { useState } from 'react';

import { useAuthErrorMessage, useReportLoginFailure } from '@neo.tax/onboarding/hooks';
import { StyledGeneralFormError, SubmitButton } from '@neo.tax/onboarding/organisms';
import { PROVISION_CURRENT_USER } from '@neo.tax/shared/graphql/documents.graphql';
import { ProvisionCurrentUserMutation, ProvisionCurrentUserMutationVariables } from '@neo.tax/shared/graphql/types';
import { AuthenticationState } from '@neo.tax/shared/state';
import { deleteRefusedNewAccount, readProvisioningErrorReason } from '@neo.tax/shared/third-parties';

const PROVISION_ERROR = "We couldn't finish setting up your account. Please try again.";

/**
 * Shown on /signup when the visitor is authenticated in Firebase but has no DB user yet — an orphaned
 * session (a partial signup, or an account whose DB record was removed). useAuthRedirect routes such
 * sessions here instead of the verify-email page (whose resend can't work without a DB user).
 *
 * The server provisions the DB user from the *verified ID token* (provisionCurrentUser), so no
 * password is re-entered. Refetching GetUserFromAuth flips the session to "provisioned", after which
 * useAuthRedirect forwards the user on to email verification (and this component unmounts).
 */
export const FinishSignup = (): React.ReactElement => {
  const [provisionCurrentUser] = useMutation<ProvisionCurrentUserMutation, ProvisionCurrentUserMutationVariables>(
    PROVISION_CURRENT_USER,
    { refetchQueries: ['GetUserFromAuth'], awaitRefetchQueries: true },
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The shared hook rather than local state, so a refusal reaches the same copy the sign-in form shows for it
  // rather than a second wording living here.
  const [errorMessage, setErrorMessage] = useAuthErrorMessage();
  const reportLoginFailure = useReportLoginFailure();

  const onFinish = async (): Promise<void> => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      // On success the GetUserFromAuth refetch makes the session provisioned, so useAuthRedirect
      // navigates away and unmounts this component — leave isSubmitting set so the button stays
      // disabled through the transition.
      await provisionCurrentUser();
    } catch (error) {
      // Provisioning can refuse rather than fail: the address belongs to an account this session is not the
      // sign-in identity for. Retrying cannot change that, and this is the page such a session lands on, so
      // the generic "please try again" below is the wrong advice for the likeliest reason to be here.
      const reason = readProvisioningErrorReason(error);
      if (reason) {
        // Deleted before the reason is set: ending the session unmounts this component, which would take the reason with it.
        await deleteRefusedNewAccount({ reportLoginFailure });
        AuthenticationState.updateAuthErrorType(reason);
      } else {
        setErrorMessage(PROVISION_ERROR);
      }
      setIsSubmitting(false);
    }
  };

  return (
    <Stack direction="column" gap={4} paddingY={6}>
      <Typography as="span" fontSize="sm" fontWeight="normal">
        You&apos;re signed in, but your account isn&apos;t set up yet. Finish creating it to continue.
      </Typography>
      <SubmitButton
        type="button"
        variant="primary"
        rounded="regular"
        size="base"
        disabled={isSubmitting}
        onClick={onFinish}
      >
        Finish setting up your account
      </SubmitButton>
      <StyledGeneralFormError error={!!errorMessage}>{errorMessage}</StyledGeneralFormError>
    </Stack>
  );
};
